// Historique des sauvegardes d'un routeur MikroTik, groupé par passe (`runId`) :
// une passe = un export `.rsc` + une sauvegarde binaire `.backup`.
//
// Téléchargement via `api.fetch` + blob : le jeton JWT est dans un en-tête, un
// simple lien `<a href>` n'enverrait rien et recevrait un 401.
//
// Comparaison : deux exports, diff ligne à ligne côté navigateur (Myers, sans
// dépendance). « Qu'est-ce qui a changé sur ce routeur entre ces deux dates ? »
// est la question qu'on pose après un incident.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, DatabaseBackup, GitCompare, X, RefreshCw, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { fmtBytes, fmtDateTime, mikrotikCall } from '../lib/mikrotik.jsx';

const TRIGGER_LABELS = {
  scheduled: 'Planifiée',
  manual: 'Manuelle',
  'pre-upgrade': 'Avant mise à jour',
  preupgrade: 'Avant mise à jour',
};
const triggerLabel = (t) => TRIGGER_LABELS[t] || t || '—';

// ─── Diff (Myers) ───────────────────────────────────────────────
// Rend la liste d'opérations ['=' | '-' | '+', indice] ou null si la distance
// dépasse `maxD` (deux fichiers sans rapport : on ne gèle pas l'onglet).
function myers(a, b, maxD) {
  const N = a.length;
  const M = b.length;
  const max = Math.min(N + M, maxD);
  const off = max + 1;
  const v = new Int32Array(2 * max + 3);
  const trace = [];
  let found = -1;
  for (let d = 0; d <= max && found < 0; d++) {
    // Instantané des diagonales utiles à ce tour : k dans [-d-1, d+1].
    trace.push(v.slice(off - d - 1, off + d + 2));
    for (let k = -d; k <= d; k += 2) {
      let x;
      if (k === -d || (k !== d && v[off + k - 1] < v[off + k + 1])) x = v[off + k + 1];
      else x = v[off + k - 1] + 1;
      let y = x - k;
      while (x < N && y < M && a[x] === b[y]) {
        x++;
        y++;
      }
      v[off + k] = x;
      if (x >= N && y >= M) {
        found = d;
        break;
      }
    }
  }
  if (found < 0) return null;

  const ops = [];
  let x = N;
  let y = M;
  for (let d = found; d > 0; d--) {
    const snap = trace[d];
    const get = (k) => snap[k + d + 1];
    const k = x - y;
    const prevK = k === -d || (k !== d && get(k - 1) < get(k + 1)) ? k + 1 : k - 1;
    const prevX = get(prevK);
    const prevY = prevX - prevK;
    // Point atteint juste après l'insertion / la suppression, avant la diagonale.
    const midX = prevK === k + 1 ? prevX : prevX + 1;
    const midY = midX - k;
    while (x > midX && y > midY) {
      ops.push(['=', x - 1]);
      x--;
      y--;
    }
    ops.push(prevK === k + 1 ? ['+', y - 1] : ['-', x - 1]);
    x = prevX;
    y = prevY;
  }
  while (x > 0 && y > 0) {
    ops.push(['=', x - 1]);
    x--;
    y--;
  }
  return ops.reverse();
}

// La 1re ligne d'un export est « # date by RouterOS x.y » : elle diffère
// toujours d'une passe à l'autre et noierait le résultat.
const stripHeader = (text) => {
  const lines = (text || '').replace(/\r\n/g, '\n').split('\n');
  if (lines.length && /^#\s.*by RouterOS/i.test(lines[0])) lines.shift();
  return lines;
};

/** Lignes du diff : { type: '=' | '-' | '+', text, oldNo, newNo }, ou { tooBig: true }. */
export function diffLines(textA, textB) {
  const a = stripHeader(textA);
  const b = stripHeader(textB);
  // Préfixe et suffixe communs d'abord : la plupart des exports ne diffèrent
  // que par quelques lignes, l'algorithme ne travaille alors que sur le milieu.
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);

  // Lignes -> entiers : comparaisons rapides dans la boucle de Myers.
  const ids = new Map();
  const toId = (s) => {
    let i = ids.get(s);
    if (i === undefined) {
      i = ids.size;
      ids.set(s, i);
    }
    return i;
  };
  const ia = midA.map(toId);
  const ib = midB.map(toId);
  const ops = ia.length === 0 && ib.length === 0 ? [] : myers(ia, ib, 3000);
  if (ops === null) return { tooBig: true };

  const rows = [];
  let oldNo = 1;
  let newNo = 1;
  for (let i = 0; i < start; i++) rows.push({ type: '=', text: a[i], oldNo: oldNo++, newNo: newNo++ });
  for (const [t, idx] of ops) {
    if (t === '=') rows.push({ type: '=', text: midA[idx], oldNo: oldNo++, newNo: newNo++ });
    else if (t === '-') rows.push({ type: '-', text: midA[idx], oldNo: oldNo++, newNo: null });
    else rows.push({ type: '+', text: midB[idx], oldNo: null, newNo: newNo++ });
  }
  for (let i = endA; i < a.length; i++) rows.push({ type: '=', text: a[i], oldNo: oldNo++, newNo: newNo++ });
  return { rows };
}

const ROW_CLS = {
  '+': 'bg-ok/15 text-fg-2',
  '-': 'bg-crit/15 text-fg-2',
  '=': 'text-muted',
};
const SIGN = { '+': '+', '-': '−', '=': ' ' };

// Garde les lignes modifiées et `ctx` lignes autour, avec des séparateurs.
function collapseContext(rows, ctx = 3) {
  const keep = new Array(rows.length).fill(false);
  rows.forEach((r, i) => {
    if (r.type === '=') return;
    for (let j = Math.max(0, i - ctx); j <= Math.min(rows.length - 1, i + ctx); j++) keep[j] = true;
  });
  const out = [];
  let skipped = 0;
  rows.forEach((r, i) => {
    if (keep[i]) {
      if (skipped) out.push({ type: 'gap', count: skipped });
      skipped = 0;
      out.push(r);
    } else skipped++;
  });
  if (skipped) out.push({ type: 'gap', count: skipped });
  return out;
}

// ─── Modale de comparaison ───
function CompareModal({ older, newer, onClose }) {
  const [state, setState] = useState({ loading: true, error: null, result: null });
  const [onlyDiff, setOnlyDiff] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      const [ra, rb] = await Promise.all([
        mikrotikCall('/platform/mikrotik/backups/' + older.id + '/content'),
        mikrotikCall('/platform/mikrotik/backups/' + newer.id + '/content'),
      ]);
      if (!active) return;
      const bad = [ra, rb].find((r) => !r.ok);
      if (bad) {
        setState({ loading: false, error: bad.json?.error || 'Impossible de lire une des sauvegardes.', result: null });
        return;
      }
      // Calcul lourd hors du rendu, après le premier affichage de l'attente.
      setTimeout(() => {
        if (!active) return;
        setState({ loading: false, error: null, result: diffLines(ra.json?.text, rb.json?.text) });
      }, 0);
    })();
    return () => {
      active = false;
    };
  }, [older.id, newer.id]);

  const rows = state.result?.rows;
  const stats = useMemo(() => {
    if (!rows) return null;
    return { add: rows.filter((r) => r.type === '+').length, del: rows.filter((r) => r.type === '-').length };
  }, [rows]);
  const shown = useMemo(() => (rows ? (onlyDiff ? collapseContext(rows) : rows) : []), [rows, onlyDiff]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4">
      <div className="bg-card border border-rule rounded-2xl w-[1000px] max-w-full h-[88vh] shadow-2xl flex flex-col overflow-hidden">
        <div className="px-6 pt-5 pb-3 flex-none flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-fg flex items-center gap-2">
              <GitCompare size={17} strokeWidth={1.75} className="text-blue-400" /> Comparaison de configurations
            </h2>
            <div className="text-[12px] text-muted-2 mt-1">
              <span className="text-crit font-semibold">{'−'}</span> {fmtDateTime(older.createdAt)}
              {older.routerOsVersion ? ` (RouterOS ${older.routerOsVersion})` : ''}
              {'  →  '}
              <span className="text-ok font-semibold">+</span> {fmtDateTime(newer.createdAt)}
              {newer.routerOsVersion ? ` (RouterOS ${newer.routerOsVersion})` : ''}
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-transparent border-none text-muted cursor-pointer hover:text-fg p-1"
            aria-label="Fermer"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="px-6 pb-2 flex-none flex items-center gap-4 text-[12px]">
          {stats && (
            <span className="text-fg-2">
              <span className="text-ok font-semibold">+{stats.add}</span>{' '}
              <span className="text-crit font-semibold">{'−'}{stats.del}</span> lignes
            </span>
          )}
          <label className="flex items-center gap-1.5 text-fg-2 cursor-pointer">
            <input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} />
            Seulement les différences (avec contexte)
          </label>
        </div>

        <div className="flex-1 min-h-0 overflow-auto border-t border-rule bg-sidebar">
          {state.loading && <div className="p-6 text-muted-2 text-xs">Chargement et comparaison…</div>}
          {state.error && (
            <div className="p-6 text-crit text-xs flex items-center gap-2">
              <AlertTriangle size={14} strokeWidth={1.75} /> {state.error}
            </div>
          )}
          {state.result?.tooBig && (
            <div className="p-6 text-warn text-xs">
              Les deux configurations sont trop différentes pour un affichage ligne à ligne : télécharger les exports et
              les comparer avec un outil dédié.
            </div>
          )}
          {rows && stats.add === 0 && stats.del === 0 && (
            <div className="p-6 text-ok text-xs">Les deux configurations sont identiques (hors ligne d'en-tête).</div>
          )}
          {rows && (stats.add > 0 || stats.del > 0) && (
            <table className="w-full border-collapse font-mono text-[11.5px] leading-5">
              <tbody>
                {shown.map((r, i) =>
                  r.type === 'gap' ? (
                    <tr key={i}>
                      <td colSpan={4} className="px-3 py-0.5 text-center text-muted-3 bg-card-hi text-[11px]">
                        {'…'} {r.count} ligne{r.count > 1 ? 's' : ''} identique{r.count > 1 ? 's' : ''}
                      </td>
                    </tr>
                  ) : (
                    <tr key={i} className={ROW_CLS[r.type]}>
                      <td className="w-12 px-2 text-right text-muted-3 select-none align-top">{r.oldNo ?? ''}</td>
                      <td className="w-12 px-2 text-right text-muted-3 select-none align-top">{r.newNo ?? ''}</td>
                      <td className="w-4 px-1 select-none align-top font-bold">{SIGN[r.type]}</td>
                      <td className="px-2 whitespace-pre-wrap break-all">{r.text || ' '}</td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Panneau ───
export function MikrotikBackupsPanel({ agentId, canDownload, canRun, sectionCls, sTitleCls }) {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState([]); // ids d'exports, 2 au plus
  const [comparing, setComparing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get('/platform/mikrotik/' + agentId + '/backups?limit=60');
    setItems(r || []);
  }, [agentId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const runs = useMemo(() => {
    const map = new Map();
    for (const b of items || []) {
      const key = b.runId || b.id;
      if (!map.has(key)) map.set(key, { runId: key, createdAt: b.createdAt, files: [] });
      map.get(key).files.push(b);
    }
    return [...map.values()].sort((x, y) => new Date(y.createdAt) - new Date(x.createdAt));
  }, [items]);

  const download = async (b) => {
    const res = await api.fetch('/platform/mikrotik/backups/' + b.id + '/download');
    if (!res.ok) {
      setNotice({ tone: 'error', text: 'Téléchargement refusé ou fichier introuvable.' });
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = b.fileName || 'sauvegarde-' + b.id;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const backupNow = async () => {
    setBusy(true);
    setNotice(null);
    const { ok, json } = await mikrotikCall('/platform/mikrotik/' + agentId + '/backup', 'POST', {});
    setBusy(false);
    if (!ok) {
      setNotice({ tone: 'error', text: json?.error || 'Échec de la demande de sauvegarde.' });
      return;
    }
    setNotice({ tone: 'info', text: 'Sauvegarde demandée : elle apparaîtra ici dans quelques instants.' });
    // La commande est prise au prochain sondage ; on relit à quelques reprises.
    [8000, 20000, 45000].forEach((ms) => setTimeout(load, ms));
  };

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= 2 ? [s[1], id] : [...s, id]));

  const startCompare = () => {
    const picked = (items || []).filter((b) => selected.includes(b.id));
    if (picked.length !== 2) return;
    picked.sort((x, y) => new Date(x.createdAt) - new Date(y.createdAt));
    setComparing({ older: picked[0], newer: picked[1] });
  };

  return (
    <div className={sectionCls}>
      <div className={`${sTitleCls} justify-between`}>
        <span className="inline-flex items-center gap-2">
          <DatabaseBackup size={16} strokeWidth={1.75} /> Sauvegardes
        </span>
        <span className="inline-flex items-center gap-2">
          {canDownload && (
            <button
              type="button"
              onClick={startCompare}
              disabled={selected.length !== 2}
              title="Cocher deux exports de configuration pour les comparer"
              className="bg-rule border border-rule-3 rounded-md px-2.5 py-1 text-fg-2 text-[11px] font-semibold cursor-pointer hover:bg-rule-2 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
            >
              <GitCompare size={12} strokeWidth={1.75} /> Comparer ({selected.length}/2)
            </button>
          )}
          {canRun && (
            <button
              type="button"
              onClick={backupNow}
              disabled={busy}
              className="bg-blue-500 border-none rounded-md px-2.5 py-1 text-white text-[11px] font-semibold cursor-pointer hover:bg-blue-600 disabled:opacity-60 inline-flex items-center gap-1"
            >
              <RefreshCw size={12} strokeWidth={1.75} /> Sauvegarder maintenant
            </button>
          )}
        </span>
      </div>

      {notice && (
        <div className={`text-[12px] mb-3 ${notice.tone === 'error' ? 'text-crit' : 'text-muted'}`}>{notice.text}</div>
      )}

      {items === null ? (
        <div className="text-muted-2 text-xs">Chargement…</div>
      ) : runs.length === 0 ? (
        <div className="text-muted-2 text-xs">Aucune sauvegarde pour le moment.</div>
      ) : (
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              {['Date', 'Déclencheur', 'RouterOS', 'Fichier', 'Taille', ''].map((c, i) => (
                <th
                  key={i}
                  className="px-3 py-2 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {runs.map((run) =>
              run.files
                .slice()
                .sort((x, y) => (x.kind === 'export' ? -1 : 1) - (y.kind === 'export' ? -1 : 1))
                .map((b, i) => (
                  <tr key={b.id}>
                    <td className="px-3 py-2 text-fg-2 border-b border-sidebar whitespace-nowrap">
                      {i === 0 ? fmtDateTime(run.createdAt) : ''}
                    </td>
                    <td className="px-3 py-2 text-muted border-b border-sidebar">
                      {i === 0 ? (
                        <>
                          {triggerLabel(b.trigger)}
                          {b.requestedBy ? <span className="text-muted-2"> · {b.requestedBy}</span> : null}
                        </>
                      ) : (
                        ''
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted border-b border-sidebar font-mono">
                      {i === 0 ? b.routerOsVersion || '—' : ''}
                    </td>
                    <td className="px-3 py-2 text-fg-2 border-b border-sidebar">
                      <span className="inline-flex items-center gap-2">
                        {canDownload && b.kind === 'export' && (
                          <input
                            type="checkbox"
                            checked={selected.includes(b.id)}
                            onChange={() => toggle(b.id)}
                            title="Sélectionner pour comparer"
                          />
                        )}
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-500/10 text-muted">
                          {b.kind === 'export' ? 'Export .rsc' : 'Binaire .backup'}
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-2 text-muted border-b border-sidebar whitespace-nowrap">
                      {fmtBytes(b.sizeBytes)}
                    </td>
                    <td className="px-3 py-2 border-b border-sidebar text-right">
                      {canDownload && (
                        <button
                          type="button"
                          onClick={() => download(b)}
                          title={'Télécharger ' + (b.fileName || '')}
                          className="bg-transparent border border-rule-3 rounded-md px-2 py-0.5 text-muted text-[11px] cursor-pointer hover:text-fg-2 inline-flex items-center gap-1"
                        >
                          <Download size={11} strokeWidth={1.75} /> Télécharger
                        </button>
                      )}
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </table>
      )}

      {comparing && <CompareModal older={comparing.older} newer={comparing.newer} onClose={() => setComparing(null)} />}
    </div>
  );
}
